/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, March 29th 2022, 9:23:26 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { getFormConfiguration } from "lib/factories";
import { TaskTemplate, FORM_STATUS_CHOICES_ENUM } from "lib/schemas";
import { FormMany2OnePicker, FormInfoPopUpForm } from "form-builder/components";
import { useServiceBuilderContext } from "service-builder/providers";
import { HelperText } from "ui-widgets";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { UserTaskCommonSettings } from "./UserTaskCommonSettings";
import Stack from "@mui/material/Stack";

export const PaymentTaskSettings = () => {
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const { watch, setValue } = useOSFormContext<TaskTemplate>();

  const watchedTaskTitle = watch<keyof TaskTemplate>("title");
  const paymentForm = watch<keyof TaskTemplate>("user_action_form");

  const [usesPaymentForm, setUsesPaymentForm] = React.useState(Boolean(paymentForm));

  const onChangeUsesPaymentForm = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUsesPaymentForm((prev) => !prev);
      setValue("user_action_form", null, { shouldDirty: true });
    },
    [setValue]
  );

  const defaultNewFormConfiguration = getFormConfiguration(currentProcessDefinition.artifact_org, {
    form_type: "payment_form",
    title: `${watchedTaskTitle} Form`,
    status: FORM_STATUS_CHOICES_ENUM.Published,
    login_required: false,
    button_text: "Submit Proof of Payment",
    sites: [currentProcessDefinition.site],
  });

  return (
    <Stack spacing={2}>
      <HelperText>
        A payment task must be preceeded by a billing task (whether automated or done by a worker). Make sure to create
        the corresponding billing task for this payment.
      </HelperText>

      <FormControlLabel
        control={<Checkbox onChange={onChangeUsesPaymentForm} checked={usesPaymentForm} />}
        label={<>Use a Payment Form?</>}
      />

      {usesPaymentForm && (
        <FormMany2OnePicker<TaskTemplate>
          schema_field_key="user_action_form"
          label="Payment Form"
          apiQueryParams={{ form_type: "payment_form" }}
          renderRelatedObjectForm={({ updatePrimaryObject, handleCloseForm, formOpenState }) => (
            <FormInfoPopUpForm
              closeFormInfoPopUpFn={handleCloseForm}
              formInfoPopUpOpenState={formOpenState}
              defaultNewFormConfiguration={defaultNewFormConfiguration}
              popUpTitle="New Payment Form"
              onAPISaveFormInfoCallBackFn={(formConfiguration) => updatePrimaryObject(formConfiguration.id)}
            />
          )}
        />
      )}

      <UserTaskCommonSettings />
    </Stack>
  );
};
